import React, { useState, useEffect, useRef } from 'react';
import { getAllUsers } from '../utils/mentionUtils';
import './MentionAutocomplete.css';

const MentionAutocomplete = ({ value, onChange, onMentionSelect, placeholder, disabled, ...props }) => {
  const [users, setUsers] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [cursorPosition, setCursorPosition] = useState(0);
  const textareaRef = useRef(null);
  const suggestionsRef = useRef(null);

  useEffect(() => {
    // Load all users when component mounts
    const loadUsers = async () => {
      try {
        console.log('MentionAutocomplete: Loading users...');
        const allUsers = await getAllUsers();
        console.log('MentionAutocomplete: Loaded users:', allUsers);
        setUsers(allUsers);
      } catch (error) {
        console.error('MentionAutocomplete: Error loading users:', error);
      }
    };
    
    loadUsers();
  }, []);

  const findCurrentMention = (text, position) => {
    // Find if cursor is within a mention
    const beforeCursor = text.substring(0, position);
    const afterCursor = text.substring(position);
    
    // Look for @ before cursor
    const lastAtIndex = beforeCursor.lastIndexOf('@');
    if (lastAtIndex === -1) return null;
    
    // Make sure there's no space between @ and cursor
    const textAfterAt = beforeCursor.substring(lastAtIndex + 1);
    if (textAfterAt.includes(' ')) return null;
    
    // Find the end of the mention (space or end of text)
    const spaceAfterCursor = afterCursor.indexOf(' ');
    const mentionEnd = spaceAfterCursor === -1 ? text.length : position + spaceAfterCursor;
    
    const mentionText = text.substring(lastAtIndex + 1, mentionEnd);
    
    return {
      start: lastAtIndex,
      end: mentionEnd,
      text: mentionText
    };
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    const newCursorPosition = e.target.selectionStart;
    
    onChange(e);
    setCursorPosition(newCursorPosition);
    
    // Check if we're typing a mention
    const mention = findCurrentMention(newValue, newCursorPosition);
    
    if (mention && mention.text.length > 0) {
      // Filter users based on the mention text
      const filtered = users.filter(user => 
        user.username.toLowerCase().startsWith(mention.text.toLowerCase())
      ).slice(0, 8); // Limit to 8 suggestions
      
      setFilteredUsers(filtered);
      setShowSuggestions(filtered.length > 0);
      setSelectedIndex(0);
    } else {
      setShowSuggestions(false);
      setFilteredUsers([]);
    }
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredUsers.length - 1 ? prev + 1 : 0
        );
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : filteredUsers.length - 1
        );
        break;
        
      case 'Enter':
      case 'Tab':
        if (filteredUsers[selectedIndex]) {
          e.preventDefault();
          selectUser(filteredUsers[selectedIndex]);
        }
        break;
        
      case 'Escape':
        setShowSuggestions(false);
        break;
        
      default:
        break;
    }
  };

  const selectUser = (user) => {
    const mention = findCurrentMention(value, cursorPosition);
    if (!mention) return;
    
    const newValue = 
      value.substring(0, mention.start + 1) + // Keep the @
      user.username + 
      value.substring(mention.end);
    
    const newCursorPosition = mention.start + 1 + user.username.length;
    
    // Update the value
    const syntheticEvent = {
      target: {
        value: newValue,
        selectionStart: newCursorPosition,
        selectionEnd: newCursorPosition
      }
    };
    onChange(syntheticEvent);
    
    // Close suggestions
    setShowSuggestions(false);
    
    // Focus back to textarea and set cursor position
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    }, 0);
    
    // Call callback if provided
    if (onMentionSelect) {
      onMentionSelect(user);
    }
  };

  const handleSuggestionClick = (user) => {
    selectUser(user);
  };

  const handleFocus = () => {
    // Re-check for mentions when focused
    const mention = findCurrentMention(value, cursorPosition);
    if (mention && mention.text.length > 0) {
      const filtered = users.filter(user => 
        user.username.toLowerCase().startsWith(mention.text.toLowerCase())
      ).slice(0, 8);
      
      setFilteredUsers(filtered);
      setShowSuggestions(filtered.length > 0);
    }
  };

  const handleBlur = () => {
    // Delay hiding suggestions to allow click events
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  return (
    <div className="mention-autocomplete-container">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onClick={(e) => setCursorPosition(e.target.selectionStart)}
        onKeyUp={(e) => setCursorPosition(e.target.selectionStart)}
        placeholder={placeholder}
        disabled={disabled}
        {...props}
      />
      
      {showSuggestions && (
        <div className="mention-suggestions" ref={suggestionsRef}>
          {filteredUsers.map((user, index) => (
            <div
              key={user.email}
              className={`mention-suggestion ${index === selectedIndex ? 'selected' : ''}`}
              onClick={() => handleSuggestionClick(user)}
            >
              <div className="suggestion-avatar">
                {user.email.charAt(0).toUpperCase()}
              </div>
              <div className="suggestion-info">
                <div className="suggestion-username">@{user.username}</div>
                <div className="suggestion-email">{user.email}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MentionAutocomplete;